import { useSelector, useDispatch } from 'react-redux';
import React, { useEffect, useState } from 'react';
import {
    Grid,
    Menu,
    MenuItem,
    Paper,
    TableContainer,
    TableCell,
    TableRow,
    Typography,
    Chip,
    Avatar,
    Tooltip,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import FieldNames from '../../../defaults/FieldNames.json';
import ColorConstants from '../../../defaults/ColorConstants';
import LabelValueDisplay from '../customPanels/LabelValueDisplay';
import { formatDateForDisplay } from '../../../utils/DateHelper';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import FieldInfo from '../FieldInfo.json';
import TableBody from '@mui/material/TableBody';
import type Graphic from '@arcgis/core/Graphic';
import { isFeaturePostFire, buildFeatureDetailRoute } from '../../../utils/FeatureHelper';
import { getAppConfig, getAttributeServiceUrl } from '../../../utils/ReduxHelper';
import GeneralService from '../../../services/GeneralService';
import { useNavigate } from 'react-router';
import CancelActionDialog from '../CancelActionDialog.tsx';
import SubstituteNewAction from '../SubstituteNewAction/SubstituteNewAction.tsx';
import SubstituteExistingAction from '../SubstituteExistingAction/SubstituteExistingAction.tsx';
import { setFeature } from '../../../redux/slices/detailsSlice.jsx';
import { processSubstituteNewAction, processSubstituteExistingAction, processUncancel } from '../../../utils/CancelFeatureHelper.tsx';
import { setShowGlobalLoader } from '../../../redux/slices/globalContextSlice.jsx';
import { v4 as uuidv4 } from 'uuid';

interface CancelAssociatePanelProps {
    joinId: string;
    joinFeature: string;
}

export default function CancelAssociatePanel({
    joinId: joinId,
    joinFeature: joinFeature,
}: CancelAssociatePanelProps) {
    const config: any = getAppConfig();
    const rootServiceUrl = getAttributeServiceUrl();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const feature = useSelector((state: any) => state.details.feature);
    const mode = useSelector((state: any) => state.details.mode);
    const isTreatment = useSelector((state: any) => state.details.isTreatment);
    const userInfo  = useSelector((state: any) => state.globalContext.userInfo);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const menuOpen = Boolean(anchorEl);
    const [substitutionFeatures, setSubstitutionFeatures] = useState<Graphic[]>([]);
    const [cancelFeature, setCancelFeature] = useState<any | null>(null);
    const [substitutionForFeatures, setSubstitutionForFeatures] = useState<Graphic[]>([]);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [substituteNewDialogOpen, setSubstituteNewDialogOpen] = useState(false);
    const [substituteExistingDialogOpen, setSubstituteExistingDialogOpen] = useState(false);
    const [cancelSubEnabled,setCancelSubEnabled] = useState(false);
    const isEditMode = mode === FieldNames.modes.edit;

    const substitutionFeaturesUrl = `${rootServiceUrl}/${config.canceledFeatures[config.env].layerId}`;
    const attributesUrl = `${rootServiceUrl}/${config.actionAttributes[config.env].layerId}`;

    const handleMenuClick = (event: React.MouseEvent<HTMLDivElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleCancelUncancel = () => {
        handleMenuClose();
        setCancelDialogOpen(true);
    };

    const handleCancelSave = (data: {
        reason: string;
        subReason: string;
        notes: string;
        updatedFeature: any;
    }) => {
        // Update the feature in redux state so the rest of the UI reflects the cancellation
        if (data.updatedFeature) {
            dispatch(setFeature(data.updatedFeature));
        }

        // Refresh the canceled-feature entry details for this feature
        getCanceledFeatureDetails(Number(joinId), joinFeature);

        // Close dialog
        setCancelDialogOpen(false);
    };

    const handleUncancel = async () => {
        dispatch(setShowGlobalLoader(true));
        try {
            const featureAttr = feature?.attributes ?? feature;
            const updatedFeature = await processUncancel(featureAttr, feature, cancelFeature, userInfo, isTreatment);

            // Update the feature in redux state so the rest of the UI reflects the un-cancellation
            if (updatedFeature) {
                dispatch(setFeature(updatedFeature));
            }

            // Refresh the canceled-feature entry details for this feature
            getCanceledFeatureDetails(Number(joinId), joinFeature);
        } catch (error) {
            console.error('Error un-canceling action:', error);
        } finally {
            dispatch(setShowGlobalLoader(false));
            // Close dialog
            setCancelDialogOpen(false);
        }
    };

    const handleSubstituteNew = () => {
        handleMenuClose();
        setSubstituteNewDialogOpen(true);
    };

    const handleSubstituteNewSave = async (data: {
        selectedIds: any;
        initFunding: any;
        updatedFunding: any;
        remainingFundingRows: any;
    }) => {
        dispatch(setShowGlobalLoader(true));
        try {
            const { newFeatureAttributes } = await processSubstituteNewAction(
                feature,
                data.selectedIds,
                data.initFunding,
                data.updatedFunding,
                userInfo,
                isTreatment,
                '_SUBSTITUTE',
                data.remainingFundingRows
            );

            // Route to the newly created substitute feature
            const newFeatureRoute = buildFeatureDetailRoute(newFeatureAttributes);
            if (newFeatureRoute) {
                navigate(newFeatureRoute);
            }
        } catch (error) {
            console.error('Error substituting with new action:', error);
        } finally {
            dispatch(setShowGlobalLoader(false));
        }
    };

    const handleSubstituteExisting = () => {
        handleMenuClose();
        setSubstituteExistingDialogOpen(true);
    };

    const handleSubstituteExistingSave = async (
        selectedActionId: number,
        selectedFundingIds: number[],
        updatedFunding: any[],
        remainingFundingRows: any[],
        selectedAction: any
    ) => {
        dispatch(setShowGlobalLoader(true));
        try {
            await processSubstituteExistingAction(
                feature,
                selectedAction,
                selectedFundingIds,
                updatedFunding,
                userInfo,
                remainingFundingRows
            );

            // Route to the existing feature that was substituted in
            const existingFeatureRoute = buildFeatureDetailRoute(
                selectedAction ?? { OBJECTID: selectedActionId }
            );
            if (existingFeatureRoute) {
                navigate(existingFeatureRoute);
            }
        } catch (error) {
            console.error('Error substituting with existing action:', error);
        } finally {
            dispatch(setShowGlobalLoader(false));
        }
    };


    const queryCancelFeatures = async (
        whereClause: string,
        onSuccess: (results: any[]) => void,
        fetchRelatedDetails: {
            enabled: boolean;
            targetJoinIdField?: string;
            targetJoinFeatureField?: string;
        } = { enabled: false }
    ) => {
        try {
            const cancelResults = await GeneralService.queryCall(substitutionFeaturesUrl, {
                where: whereClause,
                outFields: '*',
                returnGeometry: false,
            });

            if (!cancelResults || cancelResults.length === 0) {
                onSuccess([]);
                return;
            }

            // If we need to fetch related feature details
            if (
                fetchRelatedDetails.enabled &&
                fetchRelatedDetails.targetJoinIdField &&
                fetchRelatedDetails.targetJoinFeatureField
            ) {
                const relatedClauses = cancelResults.map((row) => {
                    const relatedJoinId = row[fetchRelatedDetails.targetJoinIdField!];
                    const relatedJoinFeature = row[fetchRelatedDetails.targetJoinFeatureField!];
                    return `(${FieldNames.featureFields.joinFeature} = '${relatedJoinFeature}' AND ${FieldNames.featureFields.joinID} = ${relatedJoinId})`;
                });

                const featureDetails = await GeneralService.queryCall(attributesUrl, {
                    where: relatedClauses.join(' OR '),
                    outFields: '*',
                });

                onSuccess(featureDetails);
            } else {
                onSuccess(cancelResults);
            }
        } catch (error) {
            console.error('Error querying cancel features:', error);
            onSuccess([]);
        }
    };

    const getRelatedFeatures = (
        joinId: number,
        joinFeature: string,
        isSubstitutionFor: boolean,
        setFeatures: (features: Graphic[]) => void
    ) => {
        const fields = FieldNames.cancelFields;
        const sourceJoinId = isSubstitutionFor ? fields.joinIDS : fields.joinID;
        const sourceJoinFeature = isSubstitutionFor ? fields.joinFeatureS : fields.joinFeature;
        const targetJoinId = isSubstitutionFor ? fields.joinID : fields.joinIDS;
        const targetJoinFeature = isSubstitutionFor ? fields.joinFeature : fields.joinFeatureS;

        const whereClause = `${sourceJoinId} = ${joinId} AND ${sourceJoinFeature} = '${joinFeature}' AND ${targetJoinId} IS NOT NULL AND ${targetJoinFeature} IS NOT NULL`;

        queryCancelFeatures(whereClause, setFeatures, {
            enabled: true,
            targetJoinIdField: targetJoinId,
            targetJoinFeatureField: targetJoinFeature,
        });
    };

    const getCanceledFeatureDetails = (joinId: number, joinFeature: string) => {
        const whereClause = `${FieldNames.cancelFields.joinFeature} = '${joinFeature}' AND ${FieldNames.cancelFields.joinID} = ${joinId}`;

        queryCancelFeatures(whereClause, (results) => {
            setCancelFeature(results.length > 0 ? results[0] : null);
        });
    };

    useEffect(() => {
        if (joinId && joinFeature && substitutionFeaturesUrl && attributesUrl) {
            getRelatedFeatures(Number(joinId), joinFeature, false, setSubstitutionFeatures);
            getRelatedFeatures(Number(joinId), joinFeature, true, setSubstitutionForFeatures);
            getCanceledFeatureDetails(Number(joinId), joinFeature);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [joinId, joinFeature, substitutionFeaturesUrl, attributesUrl]);

    useEffect(() => {
        if(feature && mode){
            setCancelSubEnabled(isEditMode && feature?.attributes?.[FieldNames.featureFields.status]?.toLowerCase().includes('approved'));
        }
    }, [feature, mode]);

    if (!config) return null;

    const renderFeatureTable = (features: Graphic[], title: string) => {
        if (features.length === 0) return null;

        return (
            <>
                <Typography variant='h5' sx={{ textDecoration: 'underline' }}>
                    {title}
                </Typography>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{FieldInfo.Name.alias}</TableCell>
                                <TableCell>{FieldInfo.InitiationFiscalYear.alias}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {features.map((row) => (
                                <TableRow
                                    // use objectID if available, otherwise fallback to index
                                    key={row[FieldNames.featureFields.objectID] || uuidv4()}
                                    hover
                                    style={{ cursor: 'pointer' }}
                                    tabIndex={0}
                                    role='button'
                                    onClick={() => {
                                        const route = buildFeatureDetailRoute(row);
                                        if (route) navigate(route);
                                    }}
                                >
                                    <TableCell>
                                        {row[FieldNames.featureFields.name]}
                                    </TableCell>
                                    <TableCell>
                                        {
                                            row[
                                                FieldNames.featureFields.initiationFiscalYear
                                            ]
                                        }
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </>
        );
    };

    return (
        <div>
            <Tooltip title={!isEditMode ? 'Edit Mode Required' : ''}>
                <span>
                    <Chip
                        className='required-field-status-chip'
                        onClick={handleMenuClick}
                        disabled={!isEditMode}
                        aria-controls={menuOpen ? 'cancel-substitution-menu' : undefined}
                        aria-haspopup='true'
                        aria-expanded={menuOpen ? 'true' : undefined}
                        style={{
                            backgroundColor: `${ColorConstants.AvatarBackgroundColor}`,
                            marginLeft: 3,
                            zIndex: 20,
                        }}
                        avatar={
                            <Avatar
                                style={{
                                    fontSize: '12px',
                                    width: '22px',
                                    height: '22px',
                                    backgroundColor: ColorConstants.AvatarColor,
                                    color: ColorConstants.WhiteAlien,
                                }}
                            >
                                <CancelIcon />
                            </Avatar>
                        }
                        label={
                            <span style={{ fontSize: '12px', lineHeight: '21px' }}>
                                Cancel / Substitution
                            </span>
                        }
                    />
                </span>
            </Tooltip>
            <Menu
                id='cancel-substitution-menu'
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                slotProps={{
                    root: {
                        'aria-labelledby': 'cancel-substitution-button',
                    },
                }}
            >
                <MenuItem onClick={handleCancelUncancel}>
                    Cancel / Un-cancel Action (No Substitute)
                </MenuItem>
                <Tooltip title={!cancelSubEnabled ? 'Local Approval or Higher Required' : ''}>
                    <span>
                        <MenuItem disabled={!cancelSubEnabled} onClick={handleSubstituteNew}>
                            Substitute with New Action
                        </MenuItem>
                    </span>
                </Tooltip>
                <Tooltip title={!cancelSubEnabled ? 'Local Approval or Higher Required' : ''}>
                    <span>
                        <MenuItem disabled={!cancelSubEnabled} onClick={handleSubstituteExisting}>
                            Substitute with Existing Action
                        </MenuItem>
                    </span>
                </Tooltip>
            </Menu>
            <Grid
                size={12}
                container
                direction='row'
                className='grid-item'
                style={{ alignItems: 'center', minWidth: '550px', paddingRight: '5px' }}
            >
                <LabelValueDisplay
                    label='Is Cancelled?'
                    value={feature?.attributes?.[FieldNames.featureFields.isCanceled]}
                />
                <LabelValueDisplay
                    label='Cancelled Date'
                    value={feature?.attributes?.[FieldNames.featureFields.cancelledDate]}
                    formatter={formatDateForDisplay}
                />
                <LabelValueDisplay
                    label='Has Substitute?'
                    value={
                        cancelFeature?.[FieldNames.cancelFields.hasSubstitute] === FieldNames.yesNo.yes
                            ? 'Yes'
                            : 'No'
                    }
                />
            </Grid>
            {renderFeatureTable(substitutionFeatures, 'Substitution Features')}
            {renderFeatureTable(substitutionForFeatures, 'Substitution For')}

            <CancelActionDialog
                open={cancelDialogOpen}
                onClose={() => setCancelDialogOpen(false)}
                onSave={handleCancelSave}
                onUncancel={
                    feature?.attributes?.[FieldNames.featureFields.isCanceled] === FieldNames.yesNo.yes
                        ? handleUncancel
                        : undefined
                }
                cancelFeature={cancelFeature}
                editableFeature={feature}
                actionName={feature?.[FieldNames.featureFields.name]}
            />

            <SubstituteNewAction
                open={substituteNewDialogOpen}
                onClose={() => setSubstituteNewDialogOpen(false)}
                onSave={handleSubstituteNewSave}
                joinId={joinId}
                joinFeature={joinFeature}
            />

            <SubstituteExistingAction
                open={substituteExistingDialogOpen}
                onClose={() => setSubstituteExistingDialogOpen(false)}
                onSave={handleSubstituteExistingSave}
                joinId={joinId}
                joinFeature={joinFeature}
            />
        </div>
    );
}
